import { useState } from 'react'

interface Scale {
  key: string
  label: string
  description: string
  low_label: string
  high_label: string
  min: number
  max: number
}

interface Statement {
  key: string
  text: string
  min: number
  max: number
  low_label: string
  high_label: string
}

interface InstrumentSchema {
  type: 'scales' | 'likert' | 'custom'
  scales?: Scale[]
  statements?: Statement[]
  questions?: Array<{ key: string; label: string; type: string; options?: string[] }>
}

interface InstrumentRendererProps {
  schema: InstrumentSchema
  onSubmit: (responses: Record<string, number | string>) => void
  onCancel?: () => void
}

export function InstrumentRenderer({ schema, onSubmit, onCancel }: InstrumentRendererProps) {
  const [responses, setResponses] = useState<Record<string, number | string>>({})

  const setValue = (key: string, value: number | string) => {
    setResponses(prev => ({ ...prev, [key]: value }))
  }

  const isComplete = () => {
    if (schema.type === 'scales' && schema.scales) {
      return schema.scales.every(s => responses[s.key] !== undefined)
    }
    if (schema.type === 'likert' && schema.statements) {
      return schema.statements.every(s => responses[s.key] !== undefined)
    }
    return Object.keys(responses).length > 0
  }

  const handleSubmit = () => {
    if (isComplete()) {
      onSubmit(responses)
    }
  }

  if (schema.type === 'scales' && schema.scales) {
    return (
      <div className="space-y-6">
        {schema.scales.map((scale) => (
          <div key={scale.key} className="space-y-2">
            <div>
              <div className="font-medium text-sm">{scale.label}</div>
              <div className="text-xs text-[var(--color-text-secondary)]">{scale.description}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-[var(--color-text-tertiary)] w-16 text-right shrink-0">
                {scale.low_label}
              </span>
              <input
                type="range"
                min={scale.min}
                max={scale.max}
                value={responses[scale.key] as number ?? Math.floor((scale.min + scale.max) / 2)}
                onChange={(e) => setValue(scale.key, parseInt(e.target.value))}
                className="flex-1 accent-[var(--color-brand)]"
              />
              <span className="text-xs text-[var(--color-text-tertiary)] w-16 shrink-0">
                {scale.high_label}
              </span>
              <span className="text-sm font-mono w-8 text-center">
                {responses[scale.key] ?? '-'}
              </span>
            </div>
          </div>
        ))}

        <div className="flex gap-2 pt-4">
          <button
            onClick={handleSubmit}
            disabled={!isComplete()}
            className="btn btn-primary"
          >
            Submit
          </button>
          {onCancel && (
            <button onClick={onCancel} className="btn btn-secondary">
              Skip
            </button>
          )}
        </div>
      </div>
    )
  }

  if (schema.type === 'likert' && schema.statements) {
    return (
      <div className="space-y-5">
        {schema.statements.map((stmt) => (
          <div key={stmt.key} className="space-y-2">
            <div className="text-sm">{stmt.text}</div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[var(--color-text-tertiary)] w-24 text-right shrink-0">
                {stmt.low_label}
              </span>
              <div className="flex gap-1">
                {Array.from({ length: stmt.max - stmt.min + 1 }, (_, i) => stmt.min + i).map(val => (
                  <button
                    key={val}
                    onClick={() => setValue(stmt.key, val)}
                    className={`w-9 h-9 rounded text-sm font-mono border transition-colors ${
                      responses[stmt.key] === val
                        ? 'bg-[var(--color-brand)] text-white border-[var(--color-brand)]'
                        : 'bg-[var(--color-surface)] border-[var(--color-border)] hover:border-[var(--color-text-tertiary)]'
                    }`}
                  >
                    {val}
                  </button>
                ))}
              </div>
              <span className="text-xs text-[var(--color-text-tertiary)] w-24 shrink-0">
                {stmt.high_label}
              </span>
            </div>
          </div>
        ))}

        <div className="flex gap-2 pt-4">
          <button
            onClick={handleSubmit}
            disabled={!isComplete()}
            className="btn btn-primary"
          >
            Submit
          </button>
          {onCancel && (
            <button onClick={onCancel} className="btn btn-secondary">
              Skip
            </button>
          )}
        </div>
      </div>
    )
  }

  // Fallback for custom type
  return (
    <div className="text-[var(--color-text-secondary)]">
      <p>Custom instrument type — rendering not yet supported.</p>
      <button onClick={() => onSubmit({})} className="btn btn-primary mt-4">
        Continue
      </button>
    </div>
  )
}
